import { Box, Skeleton } from "@mui/material";
import React, { useMemo } from "react";
import useTheme from "../../../../../../../common/hooks/useTheme";
import { formatWeekdayShortDate } from "../../../../../../../common/utils/date";
import { Theme } from "../../../../../../../providers/ThemeProvider";
import InlineBanner from "../../../../common/InlineBanner";
import {
  activityDisplayName,
  activityZone,
  linkedEvents,
  loggedStateLabel,
} from "../../helpers/activitySelectors";
import {
  equipmentInUse,
  ExerciseLogs,
  exerciseLogsOf,
  resolveGroups,
} from "../../helpers/exerciseSelectors";
import { WORKOUT_TYPE, workoutTypeLabel } from "../../model/workoutEnums";
import {
  Activity,
  ActivityEvent,
  ExerciseGroupV2Model,
  ExerciseV1Model,
  WearableSession,
  WorkoutDetails,
} from "../../model/workoutPlanTypes";
import { useWorkoutPlan } from "../../store/useWorkoutPlan";
import { DetailWidget, DetailWidgetGrid } from "../shared/DetailWidget";
import CaloriesWidget from "./CaloriesWidget";
import EquipmentWidget from "./EquipmentWidget";
import ExerciseGroupSection from "./ExerciseGroupSection";
import WearableDataSection from "./WearableDataSection";

type Props = {
  activity: Activity;
  date: string;
};

/**
 * A planned workout's details, laid out like the mobile
 * `WorkoutDetailsScreen`: the day and the workout's name, the Calories card
 * with its progress bar, the Duration and Type tiles (plus the legacy V1
 * zone / recovery tiles), then the fetched V2 detail — the Equipment card and
 * the exercise groups — and last the "Wearable Data": the wearable sessions
 * merged into the workout, read from the legacy record's `events` as the
 * mobile screen does (the week's entries that reference the workout stand
 * in until the record arrives). A workout with a merged session carries the
 * mobile's "Automatically / Manually Logged" tag beside the day, and its
 * exercises show the logged values. The detail fetch is driven by the
 * store's selection.
 */
const WorkoutDetailsContent = ({ activity, date }: Props) => {
  const { activeDetails, activeDetailsStatus, retryDetails, getDay } = useWorkoutPlan();
  const record = activeDetails?.record ?? null;
  const sessions = useMemo<Array<WearableSession | ActivityEvent>>(
    () => (record ? record.events ?? [] : linkedEvents(getDay(date), activity.id)),
    [record, getDay, date, activity.id]
  );
  // Mobile lists only wearable sessions under "Wearable Data"; a manual log
  // shows through the exercises' logged values instead.
  const wearable = useMemo(
    () => sessions.filter((session) => session.input_source !== 0),
    [sessions]
  );
  const logs = useMemo(() => exerciseLogsOf(record), [record]);

  return (
    <>
      <Header activity={activity} date={date} loggedState={loggedStateLabel(sessions)} />
      <CaloriesWidget
        current={activity.cals?.current ?? 0}
        target={activity.cals?.target ?? 0}
      />
      <DetailWidgetGrid>
        {/* Mobile writes the duration as 77' */}
        <DetailWidget title="Duration" value={`${Math.round(activity.duration_in_minutes ?? 0)}'`} />
        <DetailWidget title="Type" value={workoutTypeLabel(activity.type)} />
      </DetailWidgetGrid>
      <DetailBody
        activity={activity}
        status={activeDetailsStatus}
        detail={activeDetails}
        logs={logs}
        isLogged={sessions.length > 0}
        onRetry={retryDetails}
      />
      <WearableDataSection events={wearable} />
    </>
  );
};

export default WorkoutDetailsContent;

type HeaderProps = {
  activity: Activity;
  date: string;
  loggedState: string | null;
};

const Header = ({ activity, date, loggedState }: HeaderProps) => {
  const theme: Theme = useTheme();
  return (
    <Box sx={styles.titleBlock}>
      <Box sx={styles.dateRow}>
        <Box sx={styles.date(theme)}>{formatWeekdayShortDate(date)}</Box>
        {loggedState && <Box sx={styles.loggedTag(theme)}>{loggedState}</Box>}
      </Box>
      <Box sx={styles.title(theme)}>{activityDisplayName(activity)}</Box>
    </Box>
  );
};

type BodyProps = {
  activity: Activity;
  status: string;
  detail: WorkoutDetails | undefined;
  logs: ExerciseLogs | null;
  isLogged: boolean;
  onRetry: () => void;
};

const DetailBody = ({ activity, status, detail, logs, isLogged, onRetry }: BodyProps) => {
  const theme: Theme = useTheme();

  if (status === "error") {
    return (
      <InlineBanner
        text="Couldn't load the workout's exercises."
        backgroundColor={theme.colorTokens.D[10]}
        iconColor={theme.colorTokens.D[70]}
        action={{ label: "Retry", onClick: onRetry }}
      />
    );
  }
  if (status === "empty") {
    return (
      <InlineBanner
        text="No exercise details for this workout."
        backgroundColor={theme.colorTokens.P[10]}
        iconColor={theme.colors_ds.PRIMARY_ICON_MAIN}
      />
    );
  }
  if (status !== "ready" || !detail) {
    return (
      <Box sx={styles.skeleton}>
        {[0, 1, 2].map((index) => (
          <Skeleton
            key={index}
            variant="rounded"
            animation="pulse"
            sx={{ bgcolor: theme.colorTokens.N[30], height: "116px" }}
          />
        ))}
      </Box>
    );
  }
  if (detail.version === "v2") {
    return <V2Body groups={detail.groups} logs={logs} isLogged={isLogged} />;
  }
  return detail.record ? <V1Body activity={activity} detail={detail.record} /> : null;
};

type V2BodyProps = {
  groups: ExerciseGroupV2Model[];
  logs: ExerciseLogs | null;
  isLogged: boolean;
};

const V2Body = ({ groups, logs, isLogged }: V2BodyProps) => {
  const theme: Theme = useTheme();
  const resolved = useMemo(() => resolveGroups(groups), [groups]);
  const equipment = useMemo(() => equipmentInUse(resolved), [resolved]);

  return (
    <>
      <EquipmentWidget equipment={equipment} />
      {/* The short rule the mobile screen draws between the widgets and the exercises. */}
      <Box sx={styles.divider(theme)} />
      <Box sx={styles.groups}>
        {resolved.map((group, index) => (
          <ExerciseGroupSection
            key={group.id}
            group={group}
            groupIndex={index}
            logs={logs}
            isLogged={isLogged}
          />
        ))}
      </Box>
    </>
  );
};

/**
 * Legacy workouts carry only a summary. Mobile shows the zone for Cardio and
 * Interval and the recovery time for Interval, beside the header's duration
 * and type.
 */
const V1Body = ({ activity, detail }: { activity: Activity; detail: ExerciseV1Model }) => {
  const isInterval = activity.type === WORKOUT_TYPE.Interval;
  const isCardio = activity.type === WORKOUT_TYPE.Cardio;
  const zone = isCardio
    ? detail.cardio_work_zone
    : isInterval
    ? detail.interval_work_zone
    : undefined;
  const widgets: Array<{ title: string; value: string }> = [];
  if (isCardio || isInterval) {
    widgets.push({ title: "Workout zone", value: `Zone ${zone || activityZone(activity) || "--"}` });
  }
  if (isInterval) {
    widgets.push({
      title: "Recovery time",
      value: `${detail.interval_recovery_time ?? "--"} sec`,
    });
  }
  if (detail.sets > 0) widgets.push({ title: "Sets", value: String(detail.sets) });
  if (widgets.length === 0) return null;

  return (
    <DetailWidgetGrid>
      {widgets.map((widget) => (
        <DetailWidget key={widget.title} title={widget.title} value={widget.value} />
      ))}
    </DetailWidgetGrid>
  );
};

const styles = {
  titleBlock: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "4px",
  },
  dateRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
  },
  date: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    fontFamily: "var(--font-Regular)",
    fontSize: "14px",
    lineHeight: "20px",
  }),
  title: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
    fontFamily: "var(--font-Bold)",
    fontSize: "20px",
    lineHeight: "28px",
  }),
  // The mobile `WorkoutFramedWidget` chip: grey sheet, 12px medium text.
  loggedTag: (theme: Theme) => ({
    flexShrink: 0,
    padding: "4px 8px",
    borderRadius: "4px",
    backgroundColor: theme.colors_ds.NEUTRAL_BG_ELEVATION1,
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "16px",
    whiteSpace: "nowrap" as "nowrap",
  }),
  divider: (theme: Theme) => ({
    alignSelf: "center",
    width: "150px",
    height: "1px",
    backgroundColor: theme.colors_ds.NEUTRAL_BORDER_WEAK,
  }),
  groups: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "24px",
  },
  skeleton: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "12px",
  },
};
